import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";
import { useQuery } from "urql";

import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { graphql } from "@/gen/gql";
import useDebounce from "@/hooks/useDebounce";
import { useSearchTournamentSeries } from "@/hooks/useSearchTournamentSeries";
import { cn, cx } from "@/utils/cx";

const tournamentSeriesQuery = graphql(`
    query TournamentSeriesQueryById($id: UUID!) {
        tournamentSeries(id: $id) {
            id
            title
        }
    }
`);

function useTournamentSeriesId(tournamentSeriesId: string | null) {
    const [result] = useQuery({
        query: tournamentSeriesQuery,
        variables: {
            id: tournamentSeriesId!,
        },
        pause: !tournamentSeriesId,
    });
    return result.data?.tournamentSeries;
}

export const TournamentSeriesIdInput = ({
    tournamentSeriesId,
    onTournamentSeriesIdChange,
    className,
}: {
    tournamentSeriesId: string | null;
    onTournamentSeriesIdChange: (tournamentSeriesId: string | null) => void;
    className?: string;
}) => {
    const [open, setOpen] = useState(false);

    const selectedTournamentSeries = useTournamentSeriesId(tournamentSeriesId);

    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 500);

    const { tournamentSeries } = useSearchTournamentSeries({
        query: debouncedQuery,
        pause: !debouncedQuery,
    });

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="justify-between"
                    >
                        {selectedTournamentSeries
                            ? selectedTournamentSeries.title
                            : "Find tournament series..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search for tournament series..."
                            value={query}
                            onInput={e => setQuery(e.currentTarget.value)}
                        />
                        <CommandList>
                            <CommandEmpty>No matching tournament series found.</CommandEmpty>
                            <CommandGroup>
                                {tournamentSeries.map(series => (
                                    <CommandItem
                                        key={series.id}
                                        value={series.id}
                                        onSelect={currentValue => {
                                            onTournamentSeriesIdChange(
                                                selectedTournamentSeries?.id === currentValue
                                                    ? null
                                                    : currentValue
                                            );
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                selectedTournamentSeries?.id === series.id
                                                    ? "opacity-100"
                                                    : "opacity-0"
                                            )}
                                        />
                                        {series.title}
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </div>
    );
};
